import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import { useState } from 'react';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import BulkTempalte941 from '@/components/ui/BulkTempalte941';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const QuarterlyFederalTax = () => {
  const [isBulkUploadTemplate, setIsBulkUploadTemplate] = useState(false);
  return (
    <AppLayout
      meta={
        <Meta
          title="Employer’s Quarterly Federal Tax Return (Form 941) for 2026"
          description="Learn everything about Form 941—the Employer’s Quarterly Federal Tax Return. Understand who must file, payment methods, due dates, mailing addresses, and more"
          keywords=""
          canonical="resources/employment-tax/employers-quarterly-federal-tax-return/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <BulkTempalte941
        isBulkUploadTemplate={isBulkUploadTemplate}
        setIsBulkUploadTemplate={setIsBulkUploadTemplate}
      />
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          Employer’s{' '}
          <span className="highlight-container">
            <span className="highlight">Quarterly Federal Tax Return</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
            textAlign: 'center',
          }}
        >
          File your Form 941 quickly and securely with TaxZerone to ensure
          <br />
          accurate reporting every quarter.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941federaltax`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/employers-quarterly-federal-tax-return/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Employer’s Quarterly Federal Tax Return at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 941</b>
                        </Link>
                        <b> </b>
                        {'is used by employers to report '}
                        <b>federal income tax withheld</b>
                        {', '}
                        <b>Social Security and Medicare (</b>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/fica-tax/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>FICA</b>
                        </Link>
                        <b>) taxes withheld</b>
                        {', and the '}
                        <b>
                          employer’s share of Social Security and Medicare taxes
                        </b>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Due Dates:</b>
                        {' Form 941 is filed quarterly and is due on '}
                        <b>April 30, July 31, October 31, and January 31</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Tax Deposits:</b>
                        {' Employers must'}
                        {' deposit'}
                        {' employment taxes through '}
                        <Link
                          href="https://www.taxzerone.com/resources/eftps/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>EFTPS</b>
                        </Link>
                        {' according to their IRS-assigned '}
                        <b>monthly or semiweekly deposit schedule</b>
                        {'. Tax deposits are separate from the quarterly '}
                        <Link
                          href="https://www.taxzerone.com/employment-tax/e-file-irs-form-941/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 941 filing
                        </Link>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Monthly Depositor:</b>
                        {' Employers with '}
                        <b>$50,000 or less</b>
                        {
                          ' in tax liability during the lookback period generally follow the monthly deposit schedule and deposit taxes by the '
                        }
                        <b>15th of the following month</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Semiweekly Depositor:</b>
                        {' Employers with '}
                        <b>more than $50,000</b>
                        {
                          ' in lookback-period tax liability generally follow the semiweekly schedule. The deposit deadline depends on the employee’s payday.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>No Wages:</b>
                        {' Employers'}
                        {' must'}
                        {' file a '}
                        <b>{'zero '}</b>
                        <b>return</b>
                        {
                          ' even if no wages were paid during the quarter, unless an applicable exception applies, such as seasonal filer status or a final return.'
                        }
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are an employer in the U.S, staying compliant with the
                  IRS requirements is important. <br />
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 941
                  </Link>{' '}
                  plays a key role in reporting taxes withheld from employee
                  wages, along with the employer’s share of taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this resource, we will guide you through all the necessary
                  information you need to know before filing Form 941.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Employer’s Quarterly Federal Tax Return?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#depost" sx={{ color: '#0202ff' }}>
                          Depositing Employment Taxes: Monthly vs Semiweekly
                          Schedule
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How can i pay the balance due on Form 941?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#do" sx={{ color: '#0202ff' }}>
                          Do i need to File IRS Form 941 if no wages were paid?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#due" sx={{ color: '#0202ff' }}>
                          Due Date for Form 941
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where should i mail Form 941?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#differs" sx={{ color: '#0202ff' }}>
                          Form 941 vs Form 940: what is the difference?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is Employer’s Quarterly Federal Tax Return?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 941
                  </Link>{' '}
                  (Employer’s Quarterly Federal Tax Return) is used by employers
                  to report:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Federal income tax withheld from employees wages </li>
                  <li>
                    Social Security and Medicare (
                    <Link
                      href="/resources/employment-tax/fica-tax/"
                      sx={{ color: '#0202ff' }}
                    >
                      FICA
                    </Link>
                    ) taxes withheld{' '}
                  </li>
                  <li>
                    The employer’s share of Social Security and Medicare
                    taxes{' '}
                  </li>
                </Box>

                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { md: 17, sm: 17, xs: 16 },
                          fontWeight: 600,
                        }}
                      >
                        Stay Compliant Every Quarter with the IRS using
                        TaxZerone!
                      </Typography>
                      <Typography variant="body1">
                        E-file your Employer’s Quarterly Federal Tax Return with
                        TaxZerone for fast, secure, and IRS-approved filing.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941federaltax`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="depost"
                >
                  Depositing Employment Taxes: Monthly vs Semiweekly Schedule
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Employers must{' '}
                  <Link
                    href="/resources/employment-tax/form-941-944-deposit-requirements/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    deposit payroll taxes
                  </Link>{' '}
                  through EFTPS ({' '}
                  <Link
                    href="https://www.eftps.gov/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    eftps.gov
                  </Link>
                  ) according to their IRS-assigned{' '}
                  <span style={{ fontWeight: 600 }}> deposit schedule</span>,
                  which is based on their{' '}
                  <span style={{ fontWeight: 600 }}>
                    lookback period liability:
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your tax liability is{' '}
                  <span style={{ fontWeight: 600 }}>$2,500 or more</span> or you
                  meet the $100,000 next-day deposit rule, you must follow the{' '}
                  <span style={{ fontWeight: 600 }}>
                    monthly or semiweekly deposit schedule
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Monthly Depositor
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you reported a total tax liability of{' '}
                    <span style={{ fontWeight: 600 }}>$50,000 or less</span> for
                    the lookback period.{' '}
                  </li>
                  <li>
                    Deposit your payroll taxes by the{' '}
                    <span style={{ fontWeight: 600 }}>
                      15th of the following month.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Semiweekly Depositor
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If your total tax liability for the lookback period is
                    <span style={{ fontWeight: 600 }}> more than $50,000</span>.
                  </li>
                  <li>Deposits depend on payday:</li>
                </Box>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            If the payday falls on
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Then deposit your taxes by the following
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Wednesday, Thursday, and/or Friday{' '}
                          </TableCell>
                          <TableCell>Wednesday</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Saturday, Sunday, Monday, and/or Tuesday{' '}
                          </TableCell>
                          <TableCell>Friday </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  Your{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    deposit schedule is not the same as your Form 941 filing due
                    date
                  </span>
                  . Filing is quarterly, but deposits must be made throughout
                  the quarter.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 941 Schedule B?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Semiweekly depositors must file{' '}
                  <Link
                    href="/resources/employment-tax/form-941-schedule-b-filing-requirements/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule B
                  </Link>{' '}
                  along with their Form 941.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    It provides a{' '}
                    <span style={{ fontWeight: 600 }}>daily breakdown</span> of
                    your tax liability.
                  </li>
                  <li>
                    The IRS uses this to match your deposits to your reported
                    liabilities.
                  </li>
                  <li>
                    Even if deposits were made correctly, an incorrect Schedule
                    B may result in IRS penalties.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="how"
                >
                  How can I pay the balance due on Form 941?
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  You can pay your balance due when filing Form 941 if the
                  following conditions are met:
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Your total taxes after adjustments and nonrefundable credits
                    (<span style={{ fontWeight: 600 }}>line 12</span>) are{' '}
                    <span style={{ fontWeight: 600 }}>less than $2,500</span>{' '}
                    for the current quarter or the prior quarter,{' '}
                    <span style={{ fontWeight: 600 }}>and</span>
                  </li>
                  <li>
                    You didn’t incur a{' '}
                    <span style={{ fontWeight: 600 }}>
                      $100,000 next-day deposit obligation
                    </span>{' '}
                    during the current quarter.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If these conditions apply, you may send your payment with Form
                  941 using several IRS - approved methods{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Electronic Federal Tax Payment System (EFTPS) </li>
                  <li>Electronic Funds Withdrawal (EFW) </li>
                  <li>Credit/debit card </li>
                  <li>Check or money order </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Electronic Federal Tax Payment System (EFTPS)
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                      {' '}
                      EFTPS
                    </Link>{' '}
                    is a is a free service offered by the U.S. Department of the
                    Treasury. It is a convenient method for businesses and
                    individuals to pay the federal taxes online or over the
                    phone.{' '}
                  </li>
                  <li>
                    Visit{' '}
                    <Link
                      href="https://www.eftps.gov/"
                      target="_blank "
                      sx={{ color: '#0202ff' }}
                    >
                      eftps.gov
                    </Link>{' '}
                    to schedule your payment with the IRS.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Electronic Funds Withdrawal (EFW)
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your Form 941 total tax due is less than $2,500, you are
                    eligible to use the{' '}
                    <Link
                      href="/resources/electronic-funds-withdrawal-efw/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      EFW
                    </Link>{' '}
                    option.
                  </li>
                  <li>
                    Provide the Bank routing number, Account type (Checking or
                    Savings), Bank account number and Taxpayer phone number. So
                    that the IRS can withdraw the payment on the selected date.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Credit or debit card
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can pay the balance due by using Credit or debit card
                  through an{' '}
                  <Link
                    href="https://www.irs.gov/payments/pay-your-taxes-by-debit-or-credit-card"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS-authorized payment processor
                  </Link>
                  .{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Check or Money Order
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If you choose to pay by paper, you can mail a check or money
                    order to the IRS. For quicker and more secure payments,
                    consider e-filing instead.{' '}
                  </li>{' '}
                  <li>
                    Mail your Form 941-V along with your check or money order to
                    the address below:{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="do"
                >
                  Do I Need to File IRS Form 941 If No Wages Were Paid?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Yes</span>, even if no
                  wages were paid to employees during the quarter, you must file
                  a “
                  <Link
                    href="https://support.taxzerone.com/how-to-file-a-zero-tax-return-for-form-941-using-taxzerone/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    zero return
                  </Link>
                  ”unless the IRS has granted you seasonal status or you have
                  filed a final return indicating your business has closed or
                  stopped paying wages.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="due"
                >
                  Due Date for Form 941
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The{' '}
                  <Link
                    href="/resources/employment-tax/form-941-filing-deadlines-and-penalties-guide/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 941 due date
                  </Link>{' '}
                  falls on the last day of the month following the end of each
                  quarter.
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Quarter
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Reporting period
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Due Date
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Quarter 1
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            January - March
                          </TableCell>
                          <TableCell>April 30* </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Quarter 2
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            April - June
                          </TableCell>
                          <TableCell>July 31* </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Quarter 3{' '}
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            July - September{' '}
                          </TableCell>
                          <TableCell>October 31* </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Quarter 4{' '}
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            October - December{' '}
                          </TableCell>
                          <TableCell>January 31* </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  * If the due date falls on a weekend or federal holiday, the
                  next business day will be considered.{' '}
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If you deposit all your taxes on time and in
                  full, you are granted an{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    automatic 10-day extension
                  </span>{' '}
                  to file your Form 941.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="where"
                >
                  Where Should I Mail Form 941?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS mailing address for Form 941 depends on your state and
                  whether you are sending a payment with your return. To find
                  the right mailing address, check out{' '}
                  <Link
                    href="/resources/employment-tax/form-941-mailing-address/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 941 mailing address
                  </Link>
                  .
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  The IRS encourages{' '}
                  <span style={{ fontWeight: 600 }}>e-filing</span>, it is
                  faster, easier, and helps you avoid mistakes.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="differs"
                >
                  Form 941 vs Form 940: What is the Difference?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let’s see the quick difference between Form 941 and{' '}
                  <Link
                    href="/resources/employment-tax/form-940-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 940
                  </Link>
                  .{' '}
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Feature
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Form 941
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Form 940
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Form Name
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Employer’s Quarterly Federal Tax Return
                          </TableCell>
                          <TableCell>
                            Employer’s Annual Federal Unemployment (
                            <Link
                              href="/resources/employment-tax/futa-tax/"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              FUTA
                            </Link>
                            ) Tax Return{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Purpose
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Reports income taxes, Social Security, and Medicare
                            <br />({' '}
                            <Link
                              href="/resources/employment-tax/fica-tax/"
                              sx={{ color: '#0202ff' }}
                            >
                              FICA tax
                            </Link>
                            ) withheld from employee wages
                          </TableCell>
                          <TableCell>
                            Reports annual federal unemployment taxes paid by
                            employer{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Filing Frequency
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Quarterly
                          </TableCell>
                          <TableCell>Annually </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Who Pays the Tax
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Both employer and employee
                          </TableCell>
                          <TableCell>Employer only </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Applicable To
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Employers with employees
                          </TableCell>
                          <TableCell>
                            Employers that paid more than $1,500 in wages on a
                            quarter or had minimum one employee in 20+ weeks of
                            the year{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            Filing Deadlines
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            <ul>
                              <li>April 30</li>
                              <li>July 31 </li>
                              <li>October 31 </li>
                              <li>January 31 </li>
                            </ul>{' '}
                          </TableCell>
                          <TableCell>January 31</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i941.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS instructions for Form 941
                        </Link>{' '}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Your Quarterly Tax Return?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File Form 941 with TaxZerone for fast and Secure processing.
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              Stay IRS-compliant with accurate and timely submissions.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941federaltax`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default QuarterlyFederalTax;
